import React, { useRef } from 'react'
import classNames from 'classnames'
import { isAndroid, isIOS } from 'react-device-detect'
import { CaptionXS } from '@/atoms/Text'
import { useTranslate } from '@/utils/translate/translate-client'

export interface DateInputProps {
  label: string
  value: string
  onChange: (value: string) => void
  onBlur?: () => void
  required?: boolean
  min?: string
  max?: string
  name?: string
  disabled?: boolean
  error?: string
  /** When true, disables the native date picker on iOS/Android, allowing manual text entry instead */
  disableNativePickerOnMobile?: boolean
}

const DATE_MASK = 'MM / DD / YYYY'

/**
 * Auto-formats date input as MM / DD / YYYY while typing.
 * Returns the display format with spaced slashes for readability.
 * @param autoAppendSeparator - When true, appends " / " after completing month or day sections
 */
function formatDisplayInput(input: string, autoAppendSeparator: boolean = false): string {
  // Remove all non-digit characters to get raw numbers
  const digits = input.replace(/\D/g, '')

  // Limit to 8 digits (MMDDYYYY)
  const limitedDigits = digits.slice(0, 8)

  // Build formatted string with spaced slashes (MM / DD / YYYY)
  let formatted = ''
  for (let i = 0; i < limitedDigits.length; i++) {
    if (i === 2 || i === 4) {
      formatted += ' / '
    }
    formatted += limitedDigits[i]
  }

  // Auto-append separator after completing month (2 digits) or day (4 digits)
  if (autoAppendSeparator && (limitedDigits.length === 2 || limitedDigits.length === 4)) {
    formatted += ' / '
  }

  return formatted
}

/**
 * Converts display format (MM / DD / YYYY) to ISO format (YYYY-MM-DD) for API.
 */
function displayToIso(displayValue: string): string {
  const digits = displayValue.replace(/\D/g, '')
  if (digits.length < 8) return '' // Incomplete date
  const mm = digits.slice(0, 2)
  const dd = digits.slice(2, 4)
  const yyyy = digits.slice(4, 8)
  return `${yyyy}-${mm}-${dd}`
}

/**
 * Converts ISO format (YYYY-MM-DD) to display format (MM / DD / YYYY) for user.
 */
function isoToDisplay(isoValue: string): string {
  if (!isoValue) return ''
  const match = isoValue.match(/^(\d{4})-(\d{2})-(\d{2})$/)
  if (!match) return isoValue // Return as-is if not valid ISO format
  const [, yyyy, mm, dd] = match
  return `${mm} / ${dd} / ${yyyy}`
}

/**
 * Returns the cursor position after auto-appending a section separator.
 * Returns null if cursor should not be repositioned.
 */
function getCursorPositionAfterSection(digitCount: number): number | null {
  // After month (2 digits): cursor at end of "MM / " (position 5)
  if (digitCount === 2) return 5
  // After day (4 digits): cursor at end of "MM / DD / " (position 10)
  if (digitCount === 4) return 10
  return null
}

export const DateInput: React.FC<DateInputProps> = ({
  label,
  value,
  onChange,
  onBlur,
  required = false,
  min,
  max,
  name,
  disabled = false,
  error,
  disableNativePickerOnMobile = false,
}) => {
  const { t } = useTranslate('common')
  const inputRef = useRef<HTMLInputElement>(null)

  const isMobileOS = isAndroid || isIOS
  const useTextInput = disableNativePickerOnMobile && isMobileOS

  // Convert ISO value to display format for the text input
  const displayValue = useTextInput ? isoToDisplay(value) : value

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!useTextInput) {
      onChange(e.target.value)
      return
    }

    const prevDigits = displayValue.replace(/\D/g, '')
    const basicFormatted = formatDisplayInput(e.target.value, false)
    const newDigits = basicFormatted.replace(/\D/g, '')

    // Check if typing forward and completing a section
    const isTypingForward = newDigits.length > prevDigits.length
    const isCompletingSection = isTypingForward && (newDigits.length === 2 || newDigits.length === 4)

    // Format with auto-append if completing a section
    const formatted = isCompletingSection ? formatDisplayInput(e.target.value, true) : basicFormatted

    // Convert complete dates to ISO format for the API, keep partial as display format
    const valueToStore = newDigits.length === 8 ? displayToIso(basicFormatted) : formatted
    onChange(valueToStore)

    // Set cursor position after the separator when auto-appended
    const cursorPosition = isCompletingSection ? getCursorPositionAfterSection(newDigits.length) : null
    if (cursorPosition !== null) {
      setTimeout(() => {
        inputRef.current?.setSelectionRange(cursorPosition, cursorPosition)
      }, 0)
    }
  }

  // Always show mask overlay when using text input mode
  const showMaskOverlay = useTextInput

  return (
    <div>
      {name && label && (
        <label htmlFor={name} className="photon-label-sm pb-1.5 font-medium">
          {label}
          {required ? '*' : ''}
        </label>
      )}
      <div className={classNames('relative mt-1', useTextInput ? 'rounded-lg bg-white' : '')}>
        {/* Mask overlay - shows the format hint behind/alongside the input */}
        {showMaskOverlay && (
          <div
            aria-hidden="true"
            className="pointer-events-none absolute inset-0 flex items-center border border-transparent p-2"
          >
            <span className="photon-paragraph-md">
              {/* Render the typed portion as invisible (to reserve space) */}
              <span className="invisible">{displayValue}</span>
              {/* Render the remaining mask portion - letters lighter, slashes same as input text */}
              {DATE_MASK.slice(displayValue.length)
                .split('')
                .map((char, index) => (
                  <span key={index} className={char === '/' ? 'text-core-gray-950' : 'text-core-gray-300'}>
                    {char}
                  </span>
                ))}
            </span>
          </div>
        )}
        <input
          ref={inputRef}
          type={useTextInput ? 'text' : 'date'}
          inputMode={useTextInput ? 'numeric' : undefined}
          id={name}
          name={name}
          value={useTextInput ? displayValue : value}
          onChange={handleChange}
          onBlur={onBlur}
          required={required}
          min={useTextInput ? undefined : min}
          max={useTextInput ? undefined : max}
          maxLength={useTextInput ? 14 : undefined}
          disabled={disabled}
          className={classNames(
            'photon-paragraph-md w-full rounded-lg border p-2 !duration-[0ms] focus-visible:ring-0 focus-visible:ring-transparent focus-visible:ring-offset-0 focus-visible:ring-offset-transparent disabled:bg-white',
            error ? '!border-error-500' : 'border-core-gray-300',
            useTextInput ? 'bg-transparent' : 'bg-white',
          )}
          placeholder={useTextInput ? '' : t('monthFormat', 'mm/dd/yyyy')}
          aria-invalid={!!error}
          aria-describedby={error ? `${name}-error` : undefined}
        />
      </div>
      <CaptionXS role="alert" className="min-h-[20px]">
        {error ? (
          <span id={`${name}-error`} className="text-error-500">
            {error}
          </span>
        ) : (
          <span>&nbsp;</span>
        )}
      </CaptionXS>
    </div>
  )
}
